/**
 * ActivityBar — narrow contour rail to the left of the Sidebar.
 *
 * v6 scope (2026-05-18): Operational (Boards), Files, and Company are
 * active contours. The legacy contours (Architect / Departments /
 * University / Constitution / Strategy / Knowledge) lifted from a
 * previous design are hidden — their backend endpoints don't exist in
 * v6 and they would error out on click. The route files still exist
 * under routes/_authenticated/ and render <NotInV6Scope/> stubs for any
 * deep link that survives in users' history.
 *
 * M1.7 (2026-05-20): "Company" contour added for org founders/admins —
 * surfaces the multi-org management UI (constitution, org chart, roles,
 * connectors, skills, templates, permissions). The contour is only
 * rendered when the caller passes `canAdminOrg=true`.
 *
 * Settings-V2 refinement (2026-06-18 → 2026-06-19, founder spec; made
 * permanent 2026-07-04): the rail reads top→bottom Conductor (CeoChat
 * button) · Inbox · Boards · Files · Brain, then at the bottom (mt-auto)
 * the instant theme toggle and the account 'S' avatar (<ProfileMenu>).
 * Company is DROPPED (every /company/* page is re-homed in the Settings-V2
 * Company group, so nothing is lost). The Conductor is a global pin at the
 * TOP of the rail (it opens a chat Sheet on every page), re-homed there
 * from the board status line. The Inbox item is the shared <MentionsInbox>
 * bell: clicking it opens the Inbox popover as a right-side flyout (no
 * navigation) and it carries the unread badge. Unifying to this one bell
 * (2026-07-11) is why the duplicate Boards-tree bell was removed.
 *
 * Monochrome rules (ADR-057): no coloured fill, no saturated chrome.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Brain,
  FolderKanban,
  FolderTree,
  GraduationCap,
  MessagesSquare,
  Moon,
  Store,
  Sun,
} from 'lucide-react';

import { env } from '../../env';
import { cn } from '../../lib/utils';
import { useThemeStore } from '../../stores/theme-store';
import { ProfileMenu } from '../settings-v2/profile-menu';
import { CeoChat } from './ceo-chat';
import { MentionsInbox } from './mentions-inbox';

export type ActivityContour =
  | 'operational'
  | 'files'
  | 'brain'
  | 'company'
  // Comms — Communication Center rail contour (route /comms). Only appears
  // when env.VITE_COMM_CENTER is ON; the OFF rail never shows it.
  | 'comms'
  // Inbox — first-class rail contour in the Settings-V2 layout only
  // (route /inbox). Never appears in the OFF rail.
  | 'inbox'
  // Legacy keys kept in the type union so deep-link sync in <Shell>
  // compiles. They never appear in the rail itself.
  | 'architect'
  | 'market'
  | 'departments'
  | 'learning'
  | 'knowledge'
  | 'strategy'
  | 'constitution';

interface ContourSpec {
  key: ActivityContour;
  label: string;
  icon: LucideIcon;
  /** True if the contour is admin-only (Company / Files). */
  adminOnly?: boolean;
}

// Settings-V2 rail — Boards · Files · Brain. The Inbox bell is rendered
// separately (the shared <MentionsInbox> popover, above Boards), and Company
// is intentionally dropped (re-homed in the Settings-V2 Company group).
const SETTINGS_V2_CONTOURS: ContourSpec[] = [
  { key: 'operational', label: 'Boards', icon: FolderKanban },
  { key: 'files', label: 'Files', icon: FolderTree, adminOnly: true },
  { key: 'brain', label: 'Brain', icon: Brain },
];

// Communication Center rail contour — appended only behind env.VITE_COMM_CENTER
// so the OFF rail is byte-identical (no icon, no entry). Routes to /comms.
const COMMS_CONTOUR: ContourSpec = { key: 'comms', label: 'Comms', icon: MessagesSquare };

// Marketplace rail contour — appended only behind env.VITE_MARKETPLACE, exactly
// the way COMMS_CONTOUR above is, so the OFF rail is byte-identical (no icon,
// no entry, no import cost). Routes to /market, whose own beforeLoad redirects
// Home on an OFF build — two independent gates rather than one, because a rail
// that hides a link is not a gate, it is a decision about pixels.
const MARKET_CONTOUR: ContourSpec = { key: 'market', label: 'Market', icon: Store };

// University rail contour — appended only behind env.VITE_UNIVERSITY, exactly
// the way MARKET_CONTOUR above is, so the OFF rail is byte-identical (no icon,
// no entry, no import cost). Routes to /university. The one difference from
// Market, stated rather than left to be discovered: /university has no layout
// route, so there is no second beforeLoad gate — the flag decides the rail
// entry and the server decides the data. Every University read is fenced on
// the active Workspace and answers 404 for a Workspace the caller is not in,
// which is the gate that actually protects anything.
const UNIVERSITY_CONTOUR: ContourSpec = {
  key: 'learning',
  label: 'University',
  icon: GraduationCap,
};

interface ActivityBarProps {
  active: ActivityContour;
  onSelect: (contour: ActivityContour) => void;
  /** When true, admin-only contours (Company / Files) are rendered. */
  canAdminOrg?: boolean;
  /**
   * When a `userId` is present we render the Conductor (CeoChat) trigger
   * pinned at the TOP of the rail (above Inbox), opening the orchestrator
   * chat Sheet from any page.
   */
  ceoUserId?: string | null;
}

/**
 * Settings-V2 (2026-06-18, founder spec): instant theme toggle pinned in the
 * rail directly ABOVE the account 'S' avatar. Sun when dark / Moon when light;
 * one click flips between dark and light (no menu). Replaces the theme item
 * that used to live inside the ProfileMenu dropdown. Mirrors the same toggle
 * semantics the dropdown used (`theme === 'dark' ? 'light' : 'dark'`).
 */
function RailThemeToggle() {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="my-0.5 flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
    >
      {isDark ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
    </button>
  );
}

export function ActivityBar({ active, onSelect, canAdminOrg, ceoUserId }: ActivityBarProps) {
  const contours = [
    ...SETTINGS_V2_CONTOURS,
    ...(env.VITE_COMM_CENTER ? [COMMS_CONTOUR] : []),
    ...(env.VITE_MARKETPLACE || env.VITE_SKILLS_STORE ? [MARKET_CONTOUR] : []),
    ...(env.VITE_UNIVERSITY ? [UNIVERSITY_CONTOUR] : []),
  ];
  const visible = contours.filter((c) => !c.adminOnly || canAdminOrg);

  return (
    <nav
      aria-label="Workspace contours"
      className="flex w-12 shrink-0 flex-col items-center border-r border-border bg-muted/20 py-2"
    >
      {/* Settings-V2 (2026-06-19, founder spec): the Conductor (CeoChat) is a
          global pin at the VERY TOP of the rail, ABOVE the Inbox contour, so it
          opens the orchestrator chat Sheet from any page (re-homed from the
          board status line). It is a button that opens a Sheet, not a
          contour-select. Styled to the rail's 28–32px icon footprint. */}
      {ceoUserId && (
        <div className="mb-1 flex flex-col items-center">
          <CeoChat
            userId={ceoUserId}
            triggerClassName="my-0.5 h-7 w-7 sm:h-7 sm:w-7"
            triggerIconClassName="h-4 w-4 sm:h-4 sm:w-4"
          />
        </div>
      )}
      {/* Inbox bell — Settings-V2 unified entry point (2026-07-11): the rail
          bell IS the shared <MentionsInbox> popover. It opens a right-side
          flyout anchored to the bell (no navigation), carries the unread
          badge, and is the single mounted inbox WS subscription on desktop.
          Sits above Boards, matching the old Inbox-first order. The duplicate
          Boards-tree bell was removed in the same change. */}
      <MentionsInbox variant="rail" />
      {visible.map((c) => {
        const Icon = c.icon;
        const isActive = c.key === active;
        return (
          <button
            key={c.key}
            type="button"
            aria-label={c.label}
            aria-pressed={isActive}
            onClick={() => onSelect(c.key)}
            title={c.label}
            className={cn(
              'relative my-0.5 flex h-8 w-8 items-center justify-center rounded transition-colors',
              isActive
                ? 'bg-foreground/5 text-foreground'
                : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
            )}
          >
            {isActive && (
              <span
                aria-hidden
                className="absolute left-[-6px] top-1.5 bottom-1.5 w-[2px] rounded-r bg-foreground"
              />
            )}
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        );
      })}

      {/* Settings-V2: the rail bottom holds, top-to-bottom, the instant
          theme toggle then the account 'S' avatar (Settings / Logout — the
          theme item moved OUT of that menu into the toggle above it). The
          gear lives in the avatar menu; the Conductor is NOT rendered here —
          it is pinned at the TOP of the rail (above Inbox), 2026-06-19. */}
      <div className="mt-auto flex flex-col items-center pt-2">
        <RailThemeToggle />
        <ProfileMenu variant="default" />
      </div>
    </nav>
  );
}
